import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/lib';
import { Button, IconButton } from '@/lib/Buttons';
import { PlusIcon } from '@/lib/Icons';
import { Input } from '@/lib/Input';
import { Select, SelectOption } from '@/lib/Selects';
import { MarketIntelligenceSidebar } from './MarketIntelligenceSidebar';
import { ReportGeneratingScreen } from './ReportGeneratingScreen';
import { MarketIntelligenceSetupData, ResearchScope } from '../types';
import { hasHttpUrlScheme } from '../utils';

interface CompetitorWebsite {
  id: string;
  url: string;
}

interface MarketIntelligenceSetupProps {
  isGenerating: boolean;
  onGenerateReport: (payload: MarketIntelligenceSetupData) => Promise<void>;
}

const MAX_COMPETITORS = 5;
let competitorWebsiteId = 0;

const createCompetitorWebsite = (): CompetitorWebsite => {
  competitorWebsiteId += 1;

  return {
    id: `competitor-${competitorWebsiteId}`,
    url: '',
  };
};

const INDUSTRY_OPTIONS = [
  { id: 'retail-ecommerce', translationKey: 'setup.industries.retailEcommerce' },
  { id: 'food-beverage', translationKey: 'setup.industries.foodBeverage' },
  { id: 'fashion-beauty', translationKey: 'setup.industries.fashionBeauty' },
  { id: 'health-wellness', translationKey: 'setup.industries.healthWellness' },
  { id: 'professional-services', translationKey: 'setup.industries.professionalServices' },
  { id: 'travel-hospitality', translationKey: 'setup.industries.travelHospitality' },
  { id: 'home-furniture', translationKey: 'setup.industries.homeFurniture' },
  { id: 'education-training', translationKey: 'setup.industries.educationTraining' },
  { id: 'automotive-mobility', translationKey: 'setup.industries.automotiveMobility' },
  { id: 'real-estate', translationKey: 'setup.industries.realEstate' },
  { id: 'manufacturing', translationKey: 'setup.industries.manufacturing' },
  { id: 'other', translationKey: 'setup.industries.other' },
] as const;

const RESEARCH_SCOPE_OPTIONS = [
  {
    id: 'local',
    translationKey: 'setup.researchScope.local',
  },
  {
    id: 'national',
    translationKey: 'setup.researchScope.national',
  },
  {
    id: 'global',
    translationKey: 'setup.researchScope.global',
  },
] as const satisfies ReadonlyArray<{
  id: ResearchScope;
  translationKey: string;
}>;

export const MarketIntelligenceSetup: FC<MarketIntelligenceSetupProps> = ({ isGenerating, onGenerateReport }) => {
  const { t } = useTranslation('marketIntelligence');
  const { t: tAll } = useTranslation('ALL');
  const [yourWebsite, setYourWebsite] = useState('');
  const [competitorWebsites, setCompetitorWebsites] = useState<CompetitorWebsite[]>(() => [createCompetitorWebsite()]);
  const [industry, setIndustry] = useState<SelectOption | null>(null);
  const [researchScope, setResearchScope] = useState<ResearchScope>('national');
  const industryOptions: SelectOption[] = useMemo(
    () => INDUSTRY_OPTIONS.map((option) => ({ id: option.id, label: t(option.translationKey) })),
    [t]
  );
  const yourWebsiteError = yourWebsite.trim().length > 0 && !hasHttpUrlScheme(yourWebsite) ? t('setup.errors.urlSchemeRequired') : '';
  const competitorWebsiteErrors = competitorWebsites.reduce<Record<string, string>>((errors, competitor) => {
    const trimmedUrl = competitor.url.trim();

    if (trimmedUrl.length > 0 && !hasHttpUrlScheme(trimmedUrl)) {
      errors[competitor.id] = t('setup.errors.urlSchemeRequired');
    }

    return errors;
  }, {});

  const handleAddCompetitor = () => {
    setCompetitorWebsites((currentCompetitors) => {
      if (currentCompetitors.length >= MAX_COMPETITORS) {
        return currentCompetitors;
      }

      return [...currentCompetitors, createCompetitorWebsite()];
    });
  };

  const handleRemoveCompetitor = (id: string) => {
    setCompetitorWebsites((currentCompetitors) => currentCompetitors.filter((competitor) => competitor.id !== id));
  };

  const handleCompetitorChange = (id: string, value: string) => {
    setCompetitorWebsites((currentCompetitors) => currentCompetitors.map((competitor) => (competitor.id === id ? { ...competitor, url: value } : competitor)));
  };

  const handleGenerateReport = async () => {
    if (yourWebsiteError || Object.keys(competitorWebsiteErrors).length > 0) {
      return;
    }

    await onGenerateReport({
      yourWebsite: yourWebsite.trim(),
      competitorWebsites: competitorWebsites.map((competitor) => competitor.url.trim()).filter((website) => website.length > 0),
      industry: industry?.label ?? '',
      researchScope,
    });
  };

  const hasWebsite = yourWebsite.trim().length > 0;
  const hasValidationErrors = Boolean(yourWebsiteError) || Object.keys(competitorWebsiteErrors).length > 0;
  const isGenerateDisabled = !hasWebsite || hasValidationErrors || isGenerating;
  const isAddCompetitorDisabled = competitorWebsites.length >= MAX_COMPETITORS;

  return (
    <div className={isGenerating ? 'w-full' : 'grid w-full grid-cols-2 gap-6'}>
      <div className='min-w-0'>
        {isGenerating ? (
          <ReportGeneratingScreen />
        ) : (
          <Card className='block rounded-lg border-primary-border bg-white p-6 drop-shadow-none'>
            <div className='mb-6 flex items-start gap-3'>
              <div className='flex h-8 w-8 items-center justify-center rounded-full bg-brand-100'>
                <span className='text-sm font-semibold text-brand-600'>✦</span>
              </div>
              <div>
                <h3 className='font-semibold text-primary-900'>{tAll('marketIntelligence')}</h3>
                <p className='text-sm text-primary-600'>{t('setup.cardSubtitle')}</p>
              </div>
            </div>

            <div className='space-y-6'>
              <div>
                <label className='mb-2 block text-sm font-semibold text-primary-900'>
                  {t('setup.yourWebsiteLabel')}
                  <span className='text-red-500'>*</span>
                </label>
                <Input
                  type='text'
                  placeholder={t('setup.yourWebsitePlaceholder')}
                  value={yourWebsite}
                  onChange={(event) => setYourWebsite(event.target.value)}
                  errorMsg={yourWebsiteError}
                />
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-primary-900'>{t('setup.competitorWebsiteLabel')}</label>
                <div className='space-y-2'>
                  {competitorWebsites.map((competitor) => (
                    <div key={competitor.id} className='flex gap-2'>
                      <Input
                        type='text'
                        placeholder={t('setup.competitorWebsitePlaceholder')}
                        value={competitor.url}
                        onChange={(event) => handleCompetitorChange(competitor.id, event.target.value)}
                        className='flex-1'
                        errorMsg={competitorWebsiteErrors[competitor.id]}
                      />
                      {competitorWebsites.length > 1 ? (
                        <Button
                          type='button'
                          intent='tertiary'
                          size='sm'
                          onClick={() => handleRemoveCompetitor(competitor.id)}
                          className='rounded-lg px-3 py-2 text-red-600 hover:bg-red-50 hover:text-red-700'
                          text={t('setup.removeCompetitor')}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                <IconButton
                  type='button'
                  intent='link-color'
                  size='sm'
                  className='mt-2'
                  leadingIcon={<PlusIcon className='size-5' />}
                  text={t('setup.addCompetitor')}
                  onClick={handleAddCompetitor}
                  disabled={isAddCompetitorDisabled}
                />
              </div>

              <div>
                <label className='mb-2 block text-sm font-semibold text-primary-900'>
                  {t('setup.industryLabel')}
                </label>
                <Select
                  items={industryOptions}
                  value={industry}
                  onChange={setIndustry}
                  placeholder={t('setup.industryPlaceholder')}
                  classNames={{ wrapper: 'w-full' }}
                />
              </div>

              <div>
                <label id='market-intelligence-research-scope-label' className='mb-3 block text-sm font-semibold text-primary-900'>
                  {t('setup.researchScopeLabel')}
                  <span className='text-red-500'>*</span>
                </label>
                <div role='radiogroup' aria-labelledby='market-intelligence-research-scope-label' className='grid grid-cols-3 gap-3'>
                  {RESEARCH_SCOPE_OPTIONS.map((scope) => (
                    <Button
                      key={scope.id}
                      type='button'
                      role='radio'
                      aria-checked={researchScope === scope.id}
                      intent='secondary'
                      size='sm'
                      onClick={() => setResearchScope(scope.id)}
                      className={`w-full justify-start rounded-lg border-2 p-3 text-left shadow-none ${
                        researchScope === scope.id ? 'border-brand-600 bg-brand-50' : 'border-primary-border bg-white hover:border-primary-300'
                      }`}
                    >
                      <span>
                        <span className='block font-semibold text-primary-900'>{t(`${scope.translationKey}.label`)}</span>
                        <span className='block text-sm font-normal text-primary-600'>{t(`${scope.translationKey}.description`)}</span>
                      </span>
                    </Button>
                  ))}
                </div>
              </div>

              <div className='text-xs text-primary-600'>{t('setup.engineHint')}</div>

              <div className='flex gap-3 pt-4'>
                <Button
                  intent='secondary'
                  size='md'
                  text={tAll('cancel')}
                  onClick={() => {
                    setYourWebsite('');
                    setCompetitorWebsites([createCompetitorWebsite()]);
                    setIndustry(null);
                    setResearchScope('national');
                  }}
                />
                <Button
                  intent='primary'
                  size='md'
                  text={t('setup.generateReport')}
                  disabled={isGenerateDisabled}
                  onClick={handleGenerateReport}
                />
              </div>
            </div>
          </Card>
        )}
      </div>

      {!isGenerating ? <MarketIntelligenceSidebar /> : null}
    </div>
  );
};
